import { Button, Icon, Modal } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { Col, Row, Table } from 'reactstrap';
import {
  OfferQuotaConflict,
  ProcessQuotaConflict,
} from './bulkOffersConflicts';

interface BulkOffersQuotaWarningModalProps {
  show: boolean;
  offerConflicts: OfferQuotaConflict[];
  processConflict: ProcessQuotaConflict | null;
  onCloseModal: () => void;
  onConfirm: () => void;
}

const headerClass =
  'fs-13 text-uppercase bg-secondary fw-400 text-white border-0';

export default function BulkOffersQuotaWarningModal({
  show,
  offerConflicts,
  processConflict,
  onCloseModal,
  onConfirm,
}: BulkOffersQuotaWarningModalProps) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const prefix = 'bulkOffers.quotaWarningModal';

  return (
    <Modal isOpen={show} toggle={onCloseModal} size="lg" unmountOnClose>
      <div className="d-flex flex-column align-items-center">
        <Icon name="warning" size="52px" color="warning" />
        <div className="mt-4 fw-600 text-black fs-22 text-center">
          {t(`${prefix}.title`)}
        </div>
        <div className="mx-auto mt-2 mb-4 fs-16 text-center">
          {t(`${prefix}.subtitle`)}
        </div>
      </div>

      {offerConflicts.length > 0 && (
        <div className="mb-4">
          <p className="fs-14 font-weight-bold text-primary mb-2">
            {t(`${prefix}.offersBlockTitle`)}
          </p>
          <div className="table-responsive border rounded">
            <Table className="mb-0 align-middle table-sm" borderless striped>
              <thead>
                <tr>
                  <th className={headerClass}>{t(`${prefix}.offerColumn`)}</th>
                  <th className={`${headerClass} text-center`}>
                    {t(`${prefix}.allocatedColumn`)}
                  </th>
                  <th className={`${headerClass} text-center`}>
                    {t(`${prefix}.declaredQuotaColumn`)}
                  </th>
                </tr>
              </thead>
              <tbody>
                {offerConflicts.map((conflict) => (
                  <tr key={conflict.offerId}>
                    <td className="fs-14">{conflict.label}</td>
                    <td className="text-center text-danger font-weight-bold">
                      {conflict.allocated}
                    </td>
                    <td className="text-center">
                      {conflict.quota ?? t(`${prefix}.noDeclaredQuota`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {processConflict && (
        <div className="mb-4">
          <p className="fs-14 font-weight-bold text-primary mb-2">
            {t(`${prefix}.processBlockTitle`)}
          </p>
          <div className="border rounded p-3">
            <div
              className="d-flex flex-wrap fs-14 mb-3"
              style={{ gap: '4px 24px' }}
            >
              <span>
                {`${t(`${prefix}.processAllocatedLabel`)}: `}
                <span className="text-danger font-weight-bold">
                  {processConflict.allocated}
                </span>
              </span>
              <span>
                {`${t(`${prefix}.processQuotaLabel`)}: `}
                <span className="font-weight-bold">{processConflict.quota}</span>
              </span>
            </div>
            {/*
              Cuando el proceso se pasa del cupo de la oferta académica ninguna
              oferta individual está en falta: el excedente sale de la suma. Sin
              este desglose el modal no muestra ninguna oferta y no hay dónde
              corregir.
            */}
            <p className="fs-13 text-uppercase fw-400 mb-1">
              {t(`${prefix}.processOffersTitle`)}
            </p>
            <div className="table-responsive">
              <Table className="mb-0 align-middle table-sm" borderless striped>
                <tbody>
                  {processConflict.offers.map((offer) => (
                    <tr key={offer.offerId}>
                      <td className="fs-14">{offer.label}</td>
                      <td
                        className="text-center font-weight-bold"
                        style={{ width: 120 }}
                      >
                        {offer.allocated}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}

      <Row>
        <Col
          xs={{ order: 2, size: 12 }}
          md={{ order: 1, size: 6 }}
          className="ml-auto"
        >
          <Button
            type="button"
            text={t('bulkOffers.cancel')}
            outlined
            onClick={onCloseModal}
            fullwidth
          />
        </Col>
        <Col xs={{ order: 1, size: 12 }} md={{ order: 2, size: 6 }}>
          <div className="pb-3 pb-md-0">
            <Button
              type="button"
              text={t('bulkOffers.save')}
              fullwidth
              onClick={onConfirm}
            />
          </div>
        </Col>
      </Row>
    </Modal>
  );
}
